import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, useCallback, useRef } from 'react'
import { api } from '@/lib/api'
import { subscribe } from '@/lib/centrifugo'
import TradingViewChart from '@/components/TradingViewChart'

export const Route = createFileRoute('/tokens/$mint')({
  component: TokenDetailPage,
})

function fmt(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toFixed(2)}`
}

interface VolumeWindow {
  label: string
  buy_count: number
  sell_count: number
  buy_vol: number
  sell_vol: number
}

function VolumePanel({ data }: { data: VolumeWindow[] | null }) {
  if (!data) return null
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
      {data.map((w) => {
        const total = w.buy_vol + w.sell_vol
        const buyPct = total > 0 ? (w.buy_vol / total) * 100 : 50
        return (
          <div key={w.label} className="card" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase' }}>{w.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{fmt(total)}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: 'var(--green)' }}>B: {w.buy_count} ({fmt(w.buy_vol)})</span>
              <span style={{ color: 'var(--red)' }}>S: {w.sell_count} ({fmt(w.sell_vol)})</span>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: 'var(--red)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${buyPct}%`, background: 'var(--green)', transition: 'width 0.3s' }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SortHeader({ label, field, current, order, onSort }: { label: string; field: string; current: string; order: string; onSort: (f: string) => void }) {
  const active = current === field
  const arrow = active ? (order === 'asc' ? ' \u25B2' : ' \u25BC') : ''
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => onSort(field)}>
      {label}{arrow}
    </th>
  )
}

function symbolStatsToWindows(info: any): VolumeWindow[] {
  return [
    { label: '5m', buy_count: info.stats_5m?.buy_count ?? 0, sell_count: info.stats_5m?.sell_count ?? 0, buy_vol: info.stats_5m?.buy_volume ?? 0, sell_vol: info.stats_5m?.sell_volume ?? 0 },
    { label: '1h', buy_count: info.stats_1h?.buy_count ?? 0, sell_count: info.stats_1h?.sell_count ?? 0, buy_vol: info.stats_1h?.buy_volume ?? 0, sell_vol: info.stats_1h?.sell_volume ?? 0 },
    { label: '4h', buy_count: info.stats_4h?.buy_count ?? 0, sell_count: info.stats_4h?.sell_count ?? 0, buy_vol: info.stats_4h?.buy_volume ?? 0, sell_vol: info.stats_4h?.sell_volume ?? 0 },
    { label: '24h', buy_count: info.stats_24h?.buy_count ?? 0, sell_count: info.stats_24h?.sell_count ?? 0, buy_vol: info.stats_24h?.buy_volume ?? 0, sell_vol: info.stats_24h?.sell_volume ?? 0 },
  ]
}

function centrifugoDataToWindows(d: any): VolumeWindow[] {
  return [
    { label: '5m', buy_count: d.buy_count_5m, sell_count: d.sell_count_5m, buy_vol: d.buy_volume_5m, sell_vol: d.sell_volume_5m },
    { label: '1h', buy_count: d.buy_count_1h, sell_count: d.sell_count_1h, buy_vol: d.buy_volume_1h, sell_vol: d.sell_volume_1h },
    { label: '4h', buy_count: d.buy_count_4h, sell_count: d.sell_count_4h, buy_vol: d.buy_volume_4h, sell_vol: d.sell_volume_4h },
    { label: '24h', buy_count: d.buy_count_24h, sell_count: d.sell_count_24h, buy_vol: d.buy_volume_24h, sell_vol: d.sell_volume_24h },
  ]
}

const TX_PER_PAGE = 50
const HOLDER_PER_PAGE = 100

function TokenDetailPage() {
  const { mint } = Route.useParams()

  const [transactions, setTransactions] = useState<any[]>([])
  const [txTotal, setTxTotal] = useState(0)
  const [txLoading, setTxLoading] = useState(false)
  const [txCursors, setTxCursors] = useState<(string | null)[]>([null])
  const [txPageIdx, setTxPageIdx] = useState(0)
  const txNextCursor = useRef<string | null>(null)

  const [holders, setHolders] = useState<any[]>([])
  const [holdersTotal, setHoldersTotal] = useState(0)
  const [holdersLoading, setHoldersLoading] = useState(false)
  const [holderSort, setHolderSort] = useState('pnl_usd')
  const [holderOrder, setHolderOrder] = useState('desc')
  const [holderCursors, setHolderCursors] = useState<(string | null)[]>([null])
  const [holderPageIdx, setHolderPageIdx] = useState(0)
  const holderNextCursor = useRef<string | null>(null)

  const [tab, setTab] = useState<'transactions' | 'holders'>('transactions')
  const [volumeData, setVolumeData] = useState<VolumeWindow[] | null>(null)

  useEffect(() => {
    api.getSymbolInfo(mint)
      .then((info) => setVolumeData(symbolStatsToWindows(info)))
      .catch(() => {})
  }, [mint])

  const fetchTransactions = useCallback((cursor: string | null) => {
    setTxLoading(true)
    api.getTransactions({ mint, per_page: TX_PER_PAGE, cursor: cursor || undefined })
      .then((res) => {
        setTransactions(res.data)
        setTxTotal(res.total)
        txNextCursor.current = res.next_cursor
      })
      .catch(() => {})
      .finally(() => setTxLoading(false))
  }, [mint])

  const fetchHolders = useCallback((cursor: string | null, sortBy: string, sortOrder: string) => {
    setHoldersLoading(true)
    api.getHolders({ mint, per_page: HOLDER_PER_PAGE, cursor: cursor || undefined, sort_by: sortBy, sort_order: sortOrder })
      .then((res) => {
        setHolders(res.data)
        setHoldersTotal(res.total)
        holderNextCursor.current = res.next_cursor
      })
      .catch(() => {})
      .finally(() => setHoldersLoading(false))
  }, [mint])

  useEffect(() => {
    setTxCursors([null])
    setTxPageIdx(0)
    fetchTransactions(null)
  }, [mint])

  useEffect(() => {
    let cleanup: (() => void) | undefined
    subscribe(`live:token:solana:${mint}`, (data: any) => {
      if (data.type === 'VOLUME_DATA') {
        setVolumeData(centrifugoDataToWindows(data.data))
        return
      }
      if (data.type === 'HOLDER_DATA_UPDATE') {
        setHolders((prev) => {
          const idx = prev.findIndex((h: any) => h.addr === data.data.addr)
          if (idx >= 0) {
            const next = [...prev]
            next[idx] = data.data
            return next
          }
          return prev
        })
        return
      }
      if (txPageIdx === 0) {
        setTransactions((prev) => {
          const next = [data, ...prev]
          return next.length > TX_PER_PAGE ? next.slice(0, TX_PER_PAGE) : next
        })
      }
    }).then((unsub) => { cleanup = unsub })
    return () => { cleanup?.() }
  }, [mint, txPageIdx])

  useEffect(() => {
    if (tab === 'holders') {
      setHolderCursors([null])
      setHolderPageIdx(0)
      fetchHolders(null, holderSort, holderOrder)
    }
  }, [tab, holderSort, holderOrder, mint])

  const handleTxNext = () => {
    if (!txNextCursor.current) return
    const newCursors = [...txCursors, txNextCursor.current]
    setTxCursors(newCursors)
    const newIdx = txPageIdx + 1
    setTxPageIdx(newIdx)
    fetchTransactions(txNextCursor.current)
  }

  const handleTxPrev = () => {
    if (txPageIdx <= 0) return
    const newIdx = txPageIdx - 1
    setTxPageIdx(newIdx)
    fetchTransactions(txCursors[newIdx])
  }

  const handleHolderNext = () => {
    if (!holderNextCursor.current) return
    const newCursors = [...holderCursors, holderNextCursor.current]
    setHolderCursors(newCursors)
    const newIdx = holderPageIdx + 1
    setHolderPageIdx(newIdx)
    fetchHolders(holderNextCursor.current, holderSort, holderOrder)
  }

  const handleHolderPrev = () => {
    if (holderPageIdx <= 0) return
    const newIdx = holderPageIdx - 1
    setHolderPageIdx(newIdx)
    fetchHolders(holderCursors[newIdx], holderSort, holderOrder)
  }

  const handleHolderSort = (field: string) => {
    if (holderSort === field) {
      setHolderOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'))
    } else {
      setHolderSort(field)
      setHolderOrder('desc')
    }
  }

  const txTotalPages = Math.max(1, Math.ceil(txTotal / TX_PER_PAGE))
  const holderTotalPages = Math.max(1, Math.ceil(holdersTotal / HOLDER_PER_PAGE))

  return (
    <div>
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>Token Detail</h1>
      <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-dim)', marginBottom: 20 }}>{mint}</div>

      <div style={{ height: 400, marginBottom: 20, border: '1px solid var(--border)', borderRadius: 8, background: '#0d0d14', overflow: 'hidden' }}>
        <TradingViewChart mint={mint} />
      </div>

      <VolumePanel data={volumeData} />

      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        <button
          onClick={() => setTab('transactions')}
          style={{
            padding: '8px 20px',
            background: 'none',
            border: 'none',
            borderBottom: tab === 'transactions' ? '2px solid var(--accent)' : '2px solid transparent',
            color: tab === 'transactions' ? 'var(--text)' : 'var(--text-dim)',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: tab === 'transactions' ? 600 : 400,
          }}
        >
          Transactions {txTotal > 0 && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>({txTotal.toLocaleString()})</span>}
        </button>
        <button
          onClick={() => setTab('holders')}
          style={{
            padding: '8px 20px',
            background: 'none',
            border: 'none',
            borderBottom: tab === 'holders' ? '2px solid var(--accent)' : '2px solid transparent',
            color: tab === 'holders' ? 'var(--text)' : 'var(--text-dim)',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: tab === 'holders' ? 600 : 400,
          }}
        >
          Holders {holdersTotal > 0 && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>({holdersTotal.toLocaleString()})</span>}
        </button>
      </div>

      {tab === 'transactions' && (
        <>
          <div style={{ maxHeight: 'calc(100vh - 620px)', overflow: 'auto', opacity: txLoading ? 0.5 : 1 }}>
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Side</th>
                  <th>Owner</th>
                  <th>Token Amount</th>
                  <th>SOL</th>
                  <th>Volume USD</th>
                  <th>Source</th>
                  <th>Signature</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx: any, i: number) => {
                  const data = tx.data || tx
                  return (
                    <tr key={data.signature || i}>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {data.ts ? new Date(data.ts).toLocaleTimeString() : '-'}
                      </td>
                      <td>
                        <span className={`badge ${data.side === 'buy' ? 'badge-green' : 'badge-red'}`}>{data.side}</span>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{(data.owner || '').slice(0, 8)}...</td>
                      <td>{data.token_amount?.toFixed(2) || '-'}</td>
                      <td>{data.sol_amount?.toFixed(4) || '-'}</td>
                      <td style={{ color: 'var(--green)' }}>${data.volume_usd?.toFixed(2) || '-'}</td>
                      <td style={{ fontSize: 12 }}>{data.source || data.platform || '-'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                        {(data.signature || '').slice(0, 12)}...
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {transactions.length === 0 && !txLoading && <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 20 }}>No transactions yet</div>}
          {txTotalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <button className="btn" disabled={txPageIdx <= 0} onClick={handleTxPrev}>Prev</button>
              <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>Page {txPageIdx + 1} / {txTotalPages}</span>
              <button className="btn" disabled={!txNextCursor.current} onClick={handleTxNext}>Next</button>
            </div>
          )}
        </>
      )}

      {tab === 'holders' && (
        <>
          {holdersLoading && holders.length === 0 ? (
            <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 20 }}>Loading holders...</div>
          ) : (
            <div style={{ maxHeight: 'calc(100vh - 620px)', overflow: 'auto', opacity: holdersLoading ? 0.5 : 1 }}>
              <table>
                <thead>
                  <tr>
                    <SortHeader label="Address" field="addr" current={holderSort} order={holderOrder} onSort={handleHolderSort} />
                    <SortHeader label="Buys" field="buy_count" current={holderSort} order={holderOrder} onSort={handleHolderSort} />
                    <SortHeader label="Sells" field="sell_count" current={holderSort} order={holderOrder} onSort={handleHolderSort} />
                    <SortHeader label="Bought ($)" field="total_bought_usd" current={holderSort} order={holderOrder} onSort={handleHolderSort} />
                    <SortHeader label="Sold ($)" field="total_sold_usd" current={holderSort} order={holderOrder} onSort={handleHolderSort} />
                    <SortHeader label="Avg Buy ($)" field="avg_buy_price_usd" current={holderSort} order={holderOrder} onSort={handleHolderSort} />
                    <SortHeader label="Avg Sell ($)" field="avg_sold_price_usd" current={holderSort} order={holderOrder} onSort={handleHolderSort} />
                    <SortHeader label="Remaining" field="remaining_token" current={holderSort} order={holderOrder} onSort={handleHolderSort} />
                    <SortHeader label="Remaining ($)" field="remaining_usd" current={holderSort} order={holderOrder} onSort={handleHolderSort} />
                    <SortHeader label="PnL ($)" field="pnl_usd" current={holderSort} order={holderOrder} onSort={handleHolderSort} />
                    <SortHeader label="PnL (SOL)" field="pnl_sol" current={holderSort} order={holderOrder} onSort={handleHolderSort} />
                  </tr>
                </thead>
                <tbody>
                  {holders.map((h: any) => (
                    <tr key={h.addr}>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{h.addr.slice(0, 8)}...</td>
                      <td>{h.buy_count}</td>
                      <td>{h.sell_count}</td>
                      <td>${h.total_bought_usd?.toFixed(2)}</td>
                      <td>${h.total_sold_usd?.toFixed(2)}</td>
                      <td>${h.avg_buy_price_usd?.toFixed(6)}</td>
                      <td>${h.avg_sold_price_usd?.toFixed(6)}</td>
                      <td>{h.remaining_token?.toFixed(2)}</td>
                      <td>${h.remaining_usd?.toFixed(2)}</td>
                      <td style={{ color: h.pnl_usd >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
                        {h.pnl_usd >= 0 ? '+' : ''}${h.pnl_usd?.toFixed(2)}
                      </td>
                      <td style={{ color: h.pnl_sol >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
                        {h.pnl_sol >= 0 ? '+' : ''}{h.pnl_sol?.toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {holders.length === 0 && !holdersLoading && <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 20 }}>No holders data</div>}
            </div>
          )}
          {holderTotalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <button className="btn" disabled={holderPageIdx <= 0} onClick={handleHolderPrev}>Prev</button>
              <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>Page {holderPageIdx + 1} / {holderTotalPages}</span>
              <button className="btn" disabled={!holderNextCursor.current} onClick={handleHolderNext}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
